import axios from "axios";
import { z } from "zod";
import { environment } from "../../../environment";
import { errorLog } from "../../../logger";
import { CurrencyCode } from "../../Accounts/requests/getClientFundingAccounts";
import { ClientId, ExternalAccountId, FartherAccountId, InstitutionId, PhysicalAccountId, PlanId, TaskId, USAState, } from "../../Types";
import ValidationError from "../../ZodError";
import { ConnectionHealth, ExternalAccountFundingStatus, ExternalAccountProvider, ExternalAccountType, FundingAccountTypeOption, LinkId, } from "./types";
import buildExternalAccountsQuery from "./utils/buildExternalAccountsQuery";
export const FundingResource = z.object({
    instructionId: z.string().uuid().optional(),
    taskId: TaskId.optional(),
    status: z.string(), //Unreliable enums
    virtualAccountId: FartherAccountId,
    physicalAccountId: PhysicalAccountId,
    externalAccountId: ExternalAccountId,
});
export const FundingDetails = z.object({
    fundingStatus: ExternalAccountFundingStatus,
    planId: PlanId.nullable(),
    validAchAccountIds: FartherAccountId.array().nullable(),
    fundingResources: FundingResource.array().nullable(),
});
export const ManualAccountDetails = z.object({
    account: z.object({
        accountType: FundingAccountTypeOption,
        accountTitle: z.string(),
        accountNumber: z.string(),
        accountHolderNames: z.string().array(),
    }),
    bank: z.object({
        routingNumber: z.string(),
        city: z.string(),
        name: z.string(),
        state: USAState,
        bankId: z.string().uuid().nullable(),
    }),
});
export const ManualAccount = z.object({
    clientId: ClientId,
    isValidForFunding: z.boolean(),
    accountId: ExternalAccountId,
    accountProvider: z.literal(ExternalAccountProvider.Enum.manual),
    attachmentUrl: z.string().url().nullable(),
    details: ManualAccountDetails,
    fundingDetails: FundingDetails.nullable(),
});
export const PlaidAccountSub = z.object({
    name: z.string(),
    routingNumber: z.string().nullable(),
    accountNumberMask: z.string().nullable(),
    availableBalance: z.number().nullable(),
    currentBalance: z.number().nullable(),
    balanceLimit: z.number().nullable(),
    officialName: z.string().nullable(),
    plaidAccountId: z.string(),
    wireNumber: z.string().nullable(),
    currencyCode: CurrencyCode,
    connectionHealthType: ConnectionHealth,
    processorToken: z.string().nullable(),
    accountNumber: z.string().nullable(),
    accountHolders: z.string().array().nullable(),
    isVisible: z.boolean(),
    accountType: ExternalAccountType,
    accountSubtype: z.string(),
});
export const PlaidAccountDetails = z.object({
    account: PlaidAccountSub,
    linkId: LinkId,
    institutionId: InstitutionId,
});
export const PlaidAccount = z.object({
    clientId: ClientId,
    isValidForFunding: z.boolean(),
    accountId: ExternalAccountId,
    accountProvider: z.literal(ExternalAccountProvider.Enum.plaid),
    details: PlaidAccountDetails,
    fundingDetails: FundingDetails.nullable(),
});
export const ExternalAccount = z.union([ManualAccount, PlaidAccount]);
const Response = z.object({
    accounts: ExternalAccount.array(),
});
export const url = (query) => {
    return environment() === "PROD"
        ? `https://middleware.prod.farther.com/external-account/v1/external-accounts${query ?? ""}`
        : `https://middleware.uat.farther.com/external-account/v1/external-accounts${query ?? ""}`;
};
/**
 * @param queryParams - allows us to get external accounts by certain requirements
 */
export default async function getExternalAccounts(queryParams, config) {
    const { data } = await axios.get(url(buildExternalAccountsQuery(queryParams)), {
        headers: {
            Client: config.clientId,
            Authorization: `Bearer ${config.jwt}`,
            ...(config.advisorId && { Advisor: config.advisorId }),
        },
    });
    const decoded = Response.safeParse(data);
    if (decoded.success) {
        return decoded.data;
    }
    else {
        const errorMessage = `[${getExternalAccounts.name}] Could not parse`;
        errorLog(errorMessage, decoded.error.message);
        throw new ValidationError(errorMessage, decoded.error.issues);
    }
}
//# sourceMappingURL=getExternalAccounts.js.map